/** Solicitudes de apoyo entre áreas: las de un caso, la bandeja «Apoyos» y sus acciones. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CaseSupportView, CreateSupportRequestInput, ListSupportRequestsInput, SupportRequestRow, SupportRequestView, SupportUnitOption } from '@pqrs/contracts';
import { apiForm, apiGet, apiPost } from '@/api/client';
import { caseKeys } from '@/features/cases/api';

export const supportKeys = {
  all: ['support'] as const,
  case: (caseId: string) => ['support', 'case', caseId] as const,
  list: (q: Partial<ListSupportRequestsInput>) => ['support', 'list', q] as const,
  count: ['support', 'count'] as const,
  units: ['support', 'units'] as const,
};

export const useCaseSupport = (caseId: string, enabled = true) => useQuery({ queryKey: supportKeys.case(caseId), queryFn: () => apiGet<CaseSupportView>(`/support-requests/cases/${caseId}`), enabled });
export const useSupportRequests = (q: Partial<ListSupportRequestsInput>) => useQuery({ queryKey: supportKeys.list(q), queryFn: () => apiGet<SupportRequestRow[]>('/support-requests', q) });
export const useSupportInboxCount = (enabled = true) => useQuery({ queryKey: supportKeys.count, queryFn: () => apiGet<{ pending: number }>('/support-requests/inbox/count'), refetchInterval: 60_000, enabled });

/** Áreas a las que se puede pedir apoyo, con si tienen quién responda. */
export const useSupportUnits = () => useQuery({ queryKey: supportKeys.units, queryFn: () => apiGet<SupportUnitOption[]>('/support-requests/units'), staleTime: 30_000 });

/** Después de pedir, responder o cancelar: el caso (sus apoyos, documentos, índice y actividad) y las bandejas. */
function useInvalidateSupport() {
  const qc = useQueryClient();
  return (caseId: string) => {
    void qc.invalidateQueries({ queryKey: supportKeys.all });
    void qc.invalidateQueries({ queryKey: caseKeys.documents(caseId) });
    void qc.invalidateQueries({ queryKey: caseKeys.detail(caseId) });
    void qc.invalidateQueries({ queryKey: caseKeys.index(caseId) });
    void qc.invalidateQueries({ queryKey: caseKeys.timeline(caseId) });
  };
}

export function useCreateSupportRequest(caseId: string) {
  const inv = useInvalidateSupport();
  return useMutation({ mutationFn: ({ input, files }: { input: CreateSupportRequestInput; files: File[] }) => apiForm<SupportRequestView[]>(`/support-requests/cases/${caseId}`, input, files), onSuccess: () => inv(caseId) });
}
export function useAnswerSupportRequest(caseId: string) {
  const inv = useInvalidateSupport();
  return useMutation({ mutationFn: ({ id, answer, files }: { id: string; answer: string; files: File[] }) => apiForm<SupportRequestView>(`/support-requests/${id}/answer`, { answer }, files), onSuccess: () => inv(caseId) });
}
export function useCancelSupportRequest(caseId: string) {
  const inv = useInvalidateSupport();
  return useMutation({ mutationFn: ({ id, reason }: { id: string; reason: string }) => apiPost<SupportRequestView>(`/support-requests/${id}/cancel`, { reason }), onSuccess: () => inv(caseId) });
}
